'use client'

import DOMPurify from 'dompurify'
import { marked } from 'marked'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef } from 'react'
import { navigatePush, openExternal } from '@/lib/utils/navigation'
import styles from './MarkdownContent.module.css'

marked.setOptions({ gfm: true, breaks: true })

interface MarkdownContentProps {
  content: string
  className?: string
  unwrapCodeFences?: boolean
}

function unwrapMarkdownCodeFences(content: string): string {
  return content.replace(/```(?:markdown|md)\s*\n([\s\S]*?)```/gi, (_, markdown: string) =>
    markdown.trim(),
  )
}

/** 后台富文本常见 inline style → 语义标签，避免 DOMPurify 剥离 style 后丢失加粗/斜体 */
function normalizeAdminRichText(content: string): string {
  return content
    .replace(
      /<span\b[^>]*\bstyle=["'][^"']*font-weight\s*:\s*(?:bold|700|bolder)[^"']*["'][^>]*>([\s\S]*?)<\/span>/gi,
      '<strong>$1</strong>',
    )
    .replace(
      /<span\b[^>]*\bstyle=["'][^"']*font-style\s*:\s*italic[^"']*["'][^>]*>([\s\S]*?)<\/span>/gi,
      '<em>$1</em>',
    )
}

function normalizeMarkdownAppLinks(content: string): string {
  return content.replace(/cuddli:\/\/\/([^\s)\]]*)/gi, (_, path: string) => {
    const normalizedPath = path ? `/${path}` : '/'
    return normalizedPath.replace(/^\/\//, '/')
  })
}

function markdownToSafeHtml(content: string, unwrapCodeFences: boolean): string {
  if (!content.trim()) return ''

  const unwrapped = unwrapCodeFences ? unwrapMarkdownCodeFences(content) : content
  const markdown = normalizeMarkdownAppLinks(normalizeAdminRichText(unwrapped))
  const parsed = marked.parse(markdown, { async: false }) as string
  return DOMPurify.sanitize(parsed, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target', 'rel', 'controls', 'playsinline', 'poster'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cuddli):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
  })
}

export function MarkdownContent({
  content,
  className = '',
  unwrapCodeFences = false,
}: MarkdownContentProps) {
  const router = useRouter()
  const contentRef = useRef<HTMLDivElement>(null)
  const html = useMemo(
    () => markdownToSafeHtml(content, unwrapCodeFences),
    [content, unwrapCodeFences],
  )

  useEffect(() => {
    const anchors = contentRef.current?.querySelectorAll('a[href]') ?? []
    const onAnchorClick = (event: Event) => {
      const anchor = event.currentTarget as HTMLAnchorElement
      const href = anchor.getAttribute('href')
      if (!href) return
      event.preventDefault()

      try {
        const url = new URL(href, window.location.origin)
        if (!['http:', 'https:'].includes(url.protocol)) return
        if (url.origin === window.location.origin) {
          navigatePush(router, `${url.pathname}${url.search}${url.hash}`)
        } else {
          openExternal(url.toString(), { newTab: true })
        }
      } catch {
        console.error('Invalid markdown URL:', href)
      }
    }

    for (const anchor of anchors) anchor.addEventListener('click', onAnchorClick)
    return () => {
      for (const anchor of anchors) anchor.removeEventListener('click', onAnchorClick)
    }
  }, [router])

  if (!html) return null

  return (
    <div
      ref={contentRef}
      className={`${styles.content} ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
